// A tab opened before a deploy still asks for the code files of the build it
// loaded. Those files are gone after the deploy, and Vercel answers every
// missing path with the app's HTML, so the request fails. Reloading once
// fetches the current build. This is the only fix: the old files are not
// coming back.
//
// How the failure reads depends on the build that is asking:
//   Vite (today)      "Failed to fetch dynamically imported module" (Chrome),
//                     "Importing a module script failed" (Safari),
//                     "error loading dynamically imported module" (Firefox),
//                     or Vite's own `vite:preloadError` event.
//   react-scripts     "ChunkLoadError" / "Loading chunk N failed", plus a
//   (before Oct 9)    SyntaxError "Unexpected token '<'" when the HTML is
//                     run as a classic script. Those tabs are served
//                     public/stale-build-reload.js instead (vercel.json).

import { reloadPage } from './pageNavigation';

const STORAGE_KEY = 'chunk_error_reload';
// One reload per this window. If the reload did not help (the file is
// really missing from the new build), the error is shown and reported
// instead of reloading forever.
export const RELOAD_GUARD_MS = 10000;

// Set once a reload has been started in THIS document, and never cleared —
// the next document is a fresh module. It exists because one failed build
// produces SEVERAL errors, and they must not be mistaken for several
// failures:
//
//   - Vite's `__vitePreload` calls its error handler once per rejected CSS
//     preload AND again for the page's own module, so a single stale route
//     dispatches `vite:preloadError` more than once.
//   - `location.reload()` is asynchronous. The browser finishes the current
//     task and its microtasks first, so React keeps rendering and throws
//     before the navigation lands.
//
// Without this flag the first event consumed the whole guard window and
// every later one read as "the reload did not help" — which turned a
// recoverable deploy into the error screen.
let reloadStarted = false;

/** Is a reload onto the current build already happening in this document? */
export function staleBuildReloadInFlight() {
  return reloadStarted;
}

const PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk/i,
  /Unable to preload CSS/i,
];

export function isStaleBuildError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = typeof error === 'string' ? error : error.message;
  return typeof message === 'string' && PATTERNS.some((re) => re.test(message));
}

/**
 * Reload once to pick up the current build.
 *
 * Returns true when a reload is HAPPENING — started by this call, or already
 * in flight from an earlier error in the same document. Callers read it as
 * "is this handled?", so a second error belonging to the same failure must
 * answer yes rather than being refused.
 *
 * Returns false only when a reload has genuinely been tried and did not help
 * (a previous document reloaded within RELOAD_GUARD_MS), or when storage is
 * unavailable — without a stamp to compare against, reloading could loop.
 */
export function reloadOnceForStaleBuild(now = Date.now()) {
  if (reloadStarted) return true;
  try {
    const last = parseInt(sessionStorage.getItem(STORAGE_KEY) || '', 10);
    if (Number.isFinite(last) && now - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    return false;
  }
  reloadStarted = true;
  // eslint-disable-next-line no-console
  console.warn('[staleBuild] This tab is running an older build; reloading to get the current one.');
  reloadPage();
  return true;
}

/**
 * Vite fires `vite:preloadError` when a lazy route's file (or one of its
 * preloads) fails to load. Reloading there means the visitor never sees the
 * error screen.
 *
 * NEVER call `event.preventDefault()` here, however tempting it looks while
 * a reload is already on its way. Vite's handler is:
 *
 *   function handlePreloadError(err) {
 *     window.dispatchEvent(e);
 *     if (!e.defaultPrevented) throw err;   // prevented ⇒ returns undefined
 *   }
 *   return baseModule().catch(handlePreloadError);
 *
 * so preventing the default makes that `.catch` RESOLVE WITH `undefined`.
 * React.lazy stores it and reads `payload._result.default` with no guard, so
 * the real error is replaced by a mystery TypeError from inside React:
 * "Cannot read properties of undefined (reading 'default')", or Safari's
 * "undefined is not an object (evaluating 'e._result.default')". That was
 * the top frontend error in Sentry (59 events on /admin alone) and it named
 * React rather than the deploy, so it read as a bug in the page.
 *
 * Letting Vite rethrow puts the honest message on React.lazy's rejection
 * path, where `isStaleBuildError` recognises it; `staleBuildReloadInFlight`
 * is what keeps it off the screen and out of Sentry while the reload lands.
 */
export function installStaleBuildRecovery(target = typeof window !== 'undefined' ? window : null) {
  if (!target || typeof target.addEventListener !== 'function') return () => {};
  const onPreloadError = () => { reloadOnceForStaleBuild(); };
  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
